import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error monitoring with cost-optimized filtering
 *
 * Strategy: Stay within free tier (5,000 errors/month) by:
 * 1. Ignoring noisy non-critical errors
 * 2. Filtering expected errors (401s, network issues)
 * 3. Sampling non-critical events
 * 4. Enriching errors with user context
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment =
    process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || "development";

  // Only initialize if DSN is provided (allows opt-in per environment)
  if (!dsn) {
    console.info("[Sentry] Skipped initialization - no DSN provided");
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Send default PII data (IP address, user info)
    // This helps identify and debug issues for specific users
    sendDefaultPii: true,

    // ===== PERFORMANCE MONITORING =====

    // Track API calls to your backend (not external services)
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.autumnplatform\.com/,
      /^https:\/\/dev\.api\.autumnplatform\.com/,
    ],

    // ===== COST OPTIMIZATION =====

    // Performance: Sample 10% of transactions in production (100% in dev)
    // This tracks page loads, API calls, and user interactions
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Replays: Disabled for now (requires @sentry/replay package)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // ===== SMART ERROR FILTERING =====

    ignoreErrors: [
      // Browser quirks (not actual bugs)
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",

      // Non-errors that are handled gracefully
      "Non-Error promise rejection captured",
      "Non-Error exception captured",

      // Deployment-related (user needs to refresh)
      "ChunkLoadError",
      "Loading chunk",
      "Failed to fetch dynamically imported module",

      // Ad blockers / browser extensions
      "blocked by client",
      "blocked by the client",

      // Cancelled requests (user navigated away)
      "The user aborted a request",
      "AbortError",
      "Request aborted",

      // Expected auth flows (already handled with toast)
      /Session expired/i,
      /Unauthorized/i,
    ],

    denyUrls: [
      // Ignore errors from browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,

      // Ignore errors from third-party scripts
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
    ],

    // ===== ERROR ENRICHMENT =====

    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out expected 401 errors (handled by client.ts interceptor)
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 401) {
          // Don't send 401s - they're expected and handled with toast
          return null;
        }

        // Sample 400 errors at 20% (usually validation, not bugs)
        if (axiosError.response?.status === 400 && Math.random() > 0.2) {
          return null;
        }
      }

      // Add user context from localStorage
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          event.user = {
            id: user.id,
            email: user.email,
            username: user.name || user.email?.split("@")[0],
          };
        }
      } catch (e) {
        // Failed to parse user - continue without user context
      }

      // Add custom context
      event.contexts = {
        ...event.contexts,
        app: {
          version: process.env.REACT_APP_VERSION || "unknown",
          environment,
        },
      };

      // Add tags for better filtering in Sentry UI
      event.tags = {
        ...event.tags,
        environment,
      };

      // Tag performance issues for easy filtering
      if (event.tags?.type === "timeout_error" || event.tags?.type === "slow_api") {
        event.tags.performance_issue = "true";
      }

      return event;
    },

    // ===== ERROR SEVERITY CLASSIFICATION =====

    beforeSendTransaction(transaction) {
      // Don't send transactions that are too fast (noise)
      if (transaction.timestamp && transaction.start_timestamp) {
        const duration = transaction.timestamp - transaction.start_timestamp;
        if (duration < 0.05) {
          // < 50ms - too fast to be meaningful
          return null;
        }
      }

      return transaction;
    },
  });

  console.info(`[Sentry] Initialized for ${environment} environment`);
}
